
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Shop } from "@/lib/supabase/shops/types";

interface ShopHeaderProps {
  shop: Shop | null;
}

const ShopHeader = ({ shop }: ShopHeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { shopSlug } = useParams<{ shopSlug: string }>();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  if (!shop) return null;
  
  const shopHomeUrl = `/shop/${shopSlug}`;
  const categoriesUrl = `/shop/${shopSlug}/categories`;

  return (
    <header
      className={cn(
        "fixed top-0 z-50 w-full transition-all duration-300",
        scrolled
          ? "bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      )}
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center">
          <Link to={shopHomeUrl} className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            {shop.logo_url ? (
              <img src={shop.logo_url} alt={shop.name} className="h-8 mr-2" />
            ) : (
              <div className="h-8 w-8 bg-indigo-600 rounded-md flex items-center justify-center text-white text-lg font-bold mr-2">
                {shop.name.charAt(0)}
              </div>
            )}
            <span>{shop.name}</span>
          </Link>
        </div>

        <nav className="hidden md:flex space-x-8">
          <Link
            to={shopHomeUrl}
            className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            Accueil
          </Link>
          <Link
            to={categoriesUrl}
            className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            Produits
          </Link>
          {shop.show_contact_form && (
            <a
              href={`${shopHomeUrl}#contact`}
              className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              Contact
            </a>
          )}
        </nav>

        <div className="flex items-center space-x-4">
          <Link to={categoriesUrl}>
            <Button variant="outline" size="sm" className="hidden sm:flex items-center">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Voir les produits
            </Button>
          </Link>
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
            )}
          </button>
        </div>
      </div>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-4">
            <nav className="flex flex-col space-y-4">
              <Link
                to={shopHomeUrl}
                className="text-base font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                onClick={() => setMobileMenuOpen(false)}
              >
                Accueil
              </Link>
              <Link
                to={categoriesUrl}
                className="text-base font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                onClick={() => setMobileMenuOpen(false)}
              >
                Produits
              </Link>
              {shop.show_contact_form && (
                <a
                  href={`${shopHomeUrl}#contact`}
                  className="text-base font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </a>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default ShopHeader;
